<template>
  <section :id="`${carouselId}-component`" :qaid="qaid" class="pdl-carousel">
    <swiper-container :id="`${carouselId}`" ref="carousel" init="false">
      <div slot="container-start" :class="['flex justify-between items-center', headerClasses]">
        <pdl-heading v-if="title && !$slots.header" :level="3" size="xl" :qaid="`${qaid}-title`">
          {{ title }}
        </pdl-heading>
        <!-- @slot Slot for custom header -->
        <slot name="header"></slot>

        <div v-show="hasNavigation && hasNavButtonsVisible" class="nav-buttons flex ml-auto">
          <pdl-button
            :class="`${carouselId}-button-prev`"
            icon="arrow_back"
            icon-only
            tertiary
            qaid="carousel-nav-button-prev"
          />
          <pdl-button
            :class="`${carouselId}-button-next`"
            icon="arrow_forward"
            icon-only
            tertiary
            qaid="carousel-nav-button-next"
          />
        </div>
      </div>
      <!-- @slot slot for slides using **pdl-carousel-slide** -->
      <slot></slot>
    </swiper-container>
  </section>
</template>

<script>
import {PdlHeading} from '@pedal/pdl-section-header';
import {PdlButton} from '@pedal/pdl-button';
import {carouselConfigs, bulletStyles} from './carousel-configs.js';
import {registerSwiper} from './swiper-register.js';

registerSwiper();

export default {
  components: {
    PdlHeading,
    PdlButton,
  },
  props: {
    /**
     * Optional QA identifier for testing purposes.
     */
    qaid: {
      type: String,
      default: 'carousel-component',
    },
    /**
     * Selects the type of preset carousel.
     * Types: slideshow, centered, ltr
     */
    carouselType: {
      type: String,
      default: 'slideshow',
      validator: (type) => ['', 'slideshow', 'centered', 'ltr'].includes(type),
    },
    /**
     * Adds a heading to your slider.
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * Custom Options object to make a more unique carousel.
     * Everything can be overwritten and all
     * usable options per the swiper docs.
     * https://swiperjs.com/swiper-api#parameters
     */
    customCarousel: {
      type: Object,
      default: () => null,
    },
    /**
     * This allows customization of the breakpoints for the Trek presets.
     * Custom object to override the breakpoints object in the carousel initialization.
     * Use this if you need further preset customization per breakpoint, but
     * look to the presets to keep things consistent around our site.
     * Refer to https://swiperjs.com/swiper-api#param-breakpoints for customization possibilities.
     */
    breakpointCustomize: {
      type: Object,
      default: () => null,
    },
    /**
     * Displays the Trek styled navigation arrows.
     */
    hasNavigation: {
      type: Boolean,
      default: false,
    },
    /**
     * Overrides custom padding to header area. (title and navigation)
     */
    headerClasses: {
      type: String,
      default: 'pb-2 px-2',
    },
  },
  setup(_, {slots}) {
    if (!slots.default) {
      console.warn(
        'Pedal Carousel: No slides provided. Please add <pdl-carousel-slide></pdl-carousel-slide> elements inside the carousel.'
      );
    }
  },
  data() {
    return {
      hasNavButtonsVisible: true,
      carouselId: `carousel-${Math.random().toString(36).substring(2, 6)}`,
    };
  },
  watch: {
    carouselId: {
      handler(value) {
        if (!value) return;
        this.$nextTick(() => {
          this.initializeCarousel();
        });
      },
      immediate: true,
    },
  },
  methods: {
    updateNavigationVisibility() {
      if (!this.$refs?.carousel?.swiper) return;
      const carousel = this.$refs.carousel.swiper;
      const slideCount = carousel.slides.length;
      const breakpoints = carousel.params.breakpoints;
      const currentBreakpoint = carousel.currentBreakpoint;

      if (slideCount <= Math.round(breakpoints[currentBreakpoint].slidesPerView)) {
        this.hasNavButtonsVisible = false;
        return;
      }
      this.hasNavButtonsVisible = true;
    },
    initializeCarousel() {
      const carouselEl = document.querySelector(`#${this.carouselId}`);
      if (!carouselEl) return;

      const carouselDefaults = {
        a11y: true,
        navigation: {
          nextEl: `.${this.carouselId}-button-next`,
          prevEl: `.${this.carouselId}-button-prev`,
        },
        keyboard: true,
        speed: 500,
        longSwipesRatio: 0.05,
        grabCursor: true,
        ...(!this.customCarousel?.injectStyles?.length
          ? {injectStyles: [bulletStyles]}
          : {injectStyles: [...bulletStyles, ...this.customCarousel.injectStyles]}),
        mousewheel: {enabled: true, forceToAxis: true, sensitivity: 0.1, thresholdDelta: 5},
      };

      let params;
      if (!this.customCarousel) {
        params = {
          ...carouselDefaults,
          ...carouselConfigs[this.carouselType],
          ...(this.breakpointCustomize && {breakpoints: this.breakpointCustomize}),
          on: {
            breakpoint: () => {
              this.$nextTick(() => {
                this.updateNavigationVisibility();
              });
            },
            slidesLengthChange: () => {
              this.$nextTick(() => {
                this.updateNavigationVisibility();
              });
            },
          },
        };
      } else {
        params = {...this.customCarousel, ...carouselDefaults};
      }

      Object.assign(carouselEl, params);
      carouselEl.initialize();

      const prevBtn = carouselEl.querySelector(`.${this.carouselId}-button-prev`);
      const nextBtn = carouselEl.querySelector(`.${this.carouselId}-button-next`);

      prevBtn?.removeAttribute('aria-controls');
      nextBtn?.removeAttribute('aria-controls');
    },
  },
};
</script>
